import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Button, Chip, CircularProgress, SvgIcon, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { FC, useCallback, useState } from 'react';
import Aumni from '../../../../assets/icons/vendor_aumni.svg?react';
import Foresight from '../../../../assets/icons/vendor_foresight.svg?react';
import { BasicDialog } from '../../../../components/Dialog/BasicDialog';
import { useToastMessageState } from '../../../../components/ToastMessage/ToastMessageProvider';
import { IFinanceRoundDataModel } from '../../../../data-models/finance-round.data-model';
import { getErrorMessage } from '../../../../services/queryHelpers';
import {
  IAumniFinancingRecord,
  IAumniTransactionRecord,
  fetchAumniRecordsForCompany,
  matchAumniRecords,
  unmatchAumniRecords,
} from '../../../../services/queries/MaggieTransactionQueries';
import { formatDate } from '../../../../util/formatters/DateFormatters';
import { formatUSDRound } from '../../../../util/formatters/NumericFormatters';
import { colors } from '../../../../theme/colors';
import { useInvalidateFinanceData } from '../../hooks/useInvalidateFinanceData';

const dialogSx = {
  '& .MuiDialog-paper': { maxWidth: 696 },
  '& .MuiDialogTitle-root': { paddingBottom: '0.625rem' },
  '& .MuiDialogTitle-root .MuiIconButton-root': { padding: '6px' },
  '& .MuiDialogTitle-root .MuiSvgIcon-root': { fontSize: 20 },
} as const;

const bodyWrapperSx = { px: '40px', pt: '12px', pb: '12px' } as const;

const footerWrapperSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  px: '40px',
  py: '32px',
} as const;

const emptyStateSx = {
  backgroundColor: 'rgba(75, 82, 93, 0.08)',
  border: `0.5px solid ${colors.border.card}`,
  borderRadius: '4px',
  py: '24px',
  px: '16px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  textAlign: 'center',
} as const;

const summaryRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  px: '12px',
  py: '8px',
  border: '1px solid #d5d7eb',
  borderRadius: '4px',
} as const;

const summaryRowContentSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  flex: 1,
  minWidth: 0,
} as const;

const vendorSvg20Sx = {
  flexShrink: 0,
  width: 20,
  height: 20,
  display: 'block',
  lineHeight: 0,
  transform: 'translateY(2px)',
  '& svg': { display: 'block' },
} as const;

const sectionSx = { mt: '20px' } as const;
const captionGapSx = { display: 'block', mb: '8px' } as const;
const rowsListSx = { display: 'flex', flexDirection: 'column', gap: '8px' } as const;
const linkButtonSx = { px: '12px', py: '4px', flexShrink: 0 } as const;
const closeButtonSx = { px: '16px', py: '6px' } as const;

interface ILinkAumniModalProps {
  open: boolean;
  onClose: () => void;
  investment: IFinanceRoundDataModel;
}

interface IRoundSummary {
  stage: string;
  date: string;
  amount: string;
}

function summarizeFinancing(f: IAumniFinancingRecord): IRoundSummary {
  return {
    stage: f.stage ?? f.new_money_equity_classes ?? f.equity_financing_id ?? f.id,
    date: f.equity_financing_date ? formatDate(f.equity_financing_date) : '',
    amount: '',
  };
}

function summarizeTransaction(t: IAumniTransactionRecord): IRoundSummary {
  return {
    stage: t.stage ?? t.equity_class ?? t.transaction_type ?? t.id,
    date: t.date ? formatDate(t.date) : '',
    amount: '',
  };
}

interface ISummaryRowProps {
  source: 'foresight' | 'aumni';
  summary: IRoundSummary;
  rightSlot?: React.ReactNode;
  excluded?: boolean;
}

function SummaryRow({ source, summary, rightSlot, excluded }: ISummaryRowProps) {
  const VendorIcon = source === 'aumni' ? Aumni : Foresight;
  return (
    <Box sx={summaryRowSx}>
      <SvgIcon inheritViewBox sx={vendorSvg20Sx} aria-hidden>
        <VendorIcon />
      </SvgIcon>
      <Box sx={summaryRowContentSx}>
        <Typography variant='body2' noWrap>
          {summary.stage}
        </Typography>
        {summary.date && (
          <Typography variant='caption' color='text.secondary' noWrap>
            {summary.date}
          </Typography>
        )}
        {summary.amount && (
          <Typography variant='caption' color='text.secondary' noWrap>
            {summary.amount}
          </Typography>
        )}
        {excluded && (
          <Typography variant='caption' color='text.secondary' noWrap>
            (excluded from auto-match)
          </Typography>
        )}
      </Box>
      {rightSlot}
    </Box>
  );
}

export const LinkAumniModal: FC<ILinkAumniModalProps> = ({ open, onClose, investment }) => {
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const { invalidateAll } = useInvalidateFinanceData();
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ['aumni-company-records', investment.companyId],
    queryFn: () => fetchAumniRecordsForCompany(investment.companyId),
    enabled: open && !!investment.companyId,
  });

  const financings = data?.financings ?? [];
  const transactions = data?.transactions ?? [];

  const handleLink = useCallback(
    async (type: 'financing' | 'transaction', id: string) => {
      setActionLoading(id);
      try {
        const payload = type === 'financing' ? { financingIds: [id] } : { transactionIds: [id] };
        await matchAumniRecords(investment.id, payload);
        pushSuccessToast({ message: 'Aumni record linked' });
        await Promise.all([refetch(), invalidateAll(investment.companyId)]);
      } catch (err) {
        pushErrorToast({ message: getErrorMessage(err, 'Failed to link Aumni record') });
      } finally {
        setActionLoading(null);
      }
    },
    [investment, refetch, invalidateAll, pushErrorToast, pushSuccessToast]
  );

  const handleUnlink = useCallback(
    async (type: 'financing' | 'transaction', id: string) => {
      setActionLoading(id);
      try {
        const payload = type === 'financing' ? { financingIds: [id] } : { transactionIds: [id] };
        await unmatchAumniRecords(investment.id, payload);
        pushSuccessToast({ message: 'Aumni record unlinked' });
        await Promise.all([refetch(), invalidateAll(investment.companyId)]);
      } catch (err) {
        pushErrorToast({ message: getErrorMessage(err, 'Failed to unlink Aumni record') });
      } finally {
        setActionLoading(null);
      }
    },
    [investment, refetch, invalidateAll, pushErrorToast, pushSuccessToast]
  );

  const linkedFinancings = financings.filter((f) => f.investmentId === investment.id);
  const availableFinancings = financings.filter((f) => f.investmentId === null);
  const linkedTransactions = transactions.filter((t) => t.investmentId === investment.id);
  const availableTransactions = transactions.filter((t) => t.investmentId === null);

  const hasLinked = linkedFinancings.length > 0 || linkedTransactions.length > 0;
  const hasAvailable = availableFinancings.length > 0 || availableTransactions.length > 0;

  const investmentSummary: IRoundSummary = {
    stage: investment.name?.trim() || 'Round',
    date: investment.roundDate ? formatDate(investment.roundDate) : '',
    amount: investment.raiseAmount != null ? formatUSDRound(investment.raiseAmount) : '',
  };

  const renderLinkedRow = (key: string, summary: IRoundSummary, onUnlink: () => void, id: string) => {
    const isLoading = actionLoading === id;
    return (
      <SummaryRow
        key={key}
        source='aumni'
        summary={summary}
        rightSlot={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '40px', flexShrink: 0 }}>
            <Chip icon={<CheckCircleIcon />} label='Linked' size='small' color='success' variant='outlined' />
            <Button
              size='small'
              variant='outlined'
              color='error'
              disabled={isLoading}
              onClick={onUnlink}
              sx={{ width: 160, flexShrink: 0 }}
            >
              {isLoading ? <CircularProgress size={14} color='error' /> : 'Unlink from Round'}
            </Button>
          </Box>
        }
      />
    );
  };

  const renderAvailableRow = (
    key: string,
    summary: IRoundSummary,
    onLink: () => void,
    id: string,
    excluded: boolean
  ) => {
    const isLoading = actionLoading === id;
    return (
      <SummaryRow
        key={key}
        source='aumni'
        summary={summary}
        excluded={excluded}
        rightSlot={
          <Button
            size='small'
            variant='contained'
            color='secondary'
            disabled={isLoading}
            onClick={onLink}
            sx={linkButtonSx}
          >
            {isLoading ? <CircularProgress size={14} /> : 'Link to Round'}
          </Button>
        }
      />
    );
  };

  return (
    <BasicDialog
      open={open}
      onClose={onClose}
      title='Link Aumni Data to Round'
      fullWidth
      maxWidth='md'
      lightDismiss
      sx={dialogSx}
    >
      <Box sx={bodyWrapperSx}>
        {isFetching && !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: '32px' }}>
            <CircularProgress size={32} />
          </Box>
        ) : isError ? (
          <Alert severity='error' sx={{ my: '16px' }}>
            Failed to load Aumni records. Please try again.
          </Alert>
        ) : (
          <>
            <Typography variant='body2' sx={{ mb: '8px' }}>
              This Round:
            </Typography>
            <SummaryRow source='foresight' summary={investmentSummary} />

            {hasLinked && (
              <Box sx={sectionSx}>
                <Typography variant='body2'>Linked to this Round:</Typography>
                <Typography variant='caption' color='text.secondary' sx={captionGapSx}>
                  If you unlink this data, it will be removed from this round and excluded from auto-matching
                </Typography>
                <Box sx={rowsListSx}>
                  {linkedFinancings.map((f) =>
                    renderLinkedRow(f.id, summarizeFinancing(f), () => handleUnlink('financing', f.id), f.id)
                  )}
                  {linkedTransactions.map((t) =>
                    renderLinkedRow(
                      t.id,
                      summarizeTransaction(t),
                      () => handleUnlink('transaction', t.id),
                      t.id
                    )
                  )}
                </Box>
              </Box>
            )}
            {hasAvailable && (
              <Box sx={sectionSx}>
                <Typography variant='body2'>Available Aumni Data:</Typography>
                <Typography variant='caption' color='text.secondary' sx={captionGapSx}>
                  This will update round metrics automatically
                </Typography>
                <Box sx={rowsListSx}>
                  {availableFinancings.map((f) =>
                    renderAvailableRow(
                      f.id,
                      summarizeFinancing(f),
                      () => handleLink('financing', f.id),
                      f.id,
                      f.matchExcluded
                    )
                  )}
                  {availableTransactions.map((t) =>
                    renderAvailableRow(
                      t.id,
                      summarizeTransaction(t),
                      () => handleLink('transaction', t.id),
                      t.id,
                      t.matchExcluded
                    )
                  )}
                </Box>
              </Box>
            )}
            {!hasLinked && !hasAvailable && (
              <Box sx={sectionSx}>
                <Box sx={emptyStateSx}>
                  <Typography variant='body2' color='text.primary'>
                    No Aumni data available for this round
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Try again after Aumni data syncs
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
      <Box sx={footerWrapperSx}>
        <Button variant='outlined' color='secondary' onClick={onClose} sx={closeButtonSx}>
          Close
        </Button>
      </Box>
    </BasicDialog>
  );
};
